import {
    AfterContentInit,
    Component,
    ContentChildren,
    Directive,
    EventEmitter,
    Input,
    NgModule,
    OnChanges,
    OnDestroy,
    Output,
    QueryList,
    SimpleChanges,
    TemplateRef,
    ViewContainerRef,
} from '@angular/core';

import {
    Context,
    DivRenderer,
    DivRendererOptions,
    Grid,
    GridNode,
    RenderContext,
    Row,
    Sort,
} from './experimental.grid';

@Directive({ selector: 'ng-template[gridContent]' })
export class GridContent {
    @Input('data-type') type: 'grid' | 'row' | 'cell';
    @Input('data-id') id: string;

    constructor(public templateRef: TemplateRef<any>) {}
}

export type SelectTemplateFn = (
    context: Context,
    contents: QueryList<GridContent>,
) => GridContent;

@Component({
    selector: '[ngGrid]',
    template: '',
    exportAs: 'grid',
})
export class ExperimentalNgGridComponent
    implements OnChanges, AfterContentInit, OnDestroy
{
    @ContentChildren(GridContent)
    private readonly _gridContents: QueryList<GridContent>;

    @Input() rendererOptions: DivRendererOptions;
    @Input() selectTemplate: SelectTemplateFn;
    @Input() grid: Grid;
    @Input() rowsTop: Row[];
    @Input() rows: Row[];
    @Input() sortDefs: Sort[];
    renderer: DivRenderer;
    gridNode: GridNode;
    @Output() inflate: EventEmitter<void>;
    private _isInflating: boolean;

    constructor(private readonly _viewContainerRef: ViewContainerRef) {
        this.inflate = new EventEmitter();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // eslint-disable-next-line no-prototype-builtins
        if (
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('rendererOptions') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('selectTemplate') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('grid')
        ) {
            this._deflate();
            this._inflate();
        }
        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('rowsTop')) {
            this._setRowsTop();
        }
        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('rows')) {
            this._setRows();
        }
        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('sortDefs')) {
            this._setSortDefs();
        }
    }

    ngAfterContentInit(): void {
        this._inflate();
    }

    ngOnDestroy(): void {
        this._deflate();
    }

    private _render(context: Context): RenderContext {
        const content = this.selectTemplate
            ? this.selectTemplate(context, this._gridContents)
            : this._gridContents.find((e) => e.type === context.t);
        if (!content || !content.templateRef) {
            return;
        }
        const ref = content.templateRef.createEmbeddedView(context);
        return {
            element: ref.rootNodes[0],
            detectChanges: () => ref.detectChanges(),
            destroy: () => ref.destroy(),
        };
    }

    private _setRowsTop(): void {
        try {
            if (!this._isInflating && this.gridNode && this.rowsTop) {
                this.gridNode.rowsTop.setChildren(this.rowsTop);
            }
        } catch (e) {
            console.warn(e);
        }
    }

    private _setRows(): void {
        try {
            if (!this._isInflating && this.gridNode && this.rows) {
                this.gridNode.setChildren(this.rows);
            }
        } catch (e) {
            console.warn(e);
        }
    }

    private _setSortDefs(): void {
        try {
            if (!this._isInflating && this.gridNode && this.sortDefs) {
                this.gridNode.sort(this.sortDefs);
            }
        } catch (e) {
            console.warn(e);
        }
    }

    private _inflate(): void {
        if (!this.rendererOptions || !this._gridContents || this._isInflating) {
            return;
        }
        this._isInflating = true;
        try {
            const rendererOpts = {
                ...this.rendererOptions,
                columns: this.rendererOptions.columns || [], // TODO remove. have underlying grid handle null/unset/missing columns
                defaultRenderFn:
                    this.rendererOptions.defaultRenderFn ||
                    ((context: Context) => this._render(context)),
            };
            this.renderer = new DivRenderer(rendererOpts);
            this.gridNode = this.renderer.grid(this.grid || {});
            this.renderer.gridNode = this.gridNode;
            this.renderer.render(this.gridNode);
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.renderer.layout();
        } catch (e) {
            console.warn(e);
        }
        try {
            const el: HTMLElement =
                this._viewContainerRef.element.nativeElement;
            el.appendChild(this.gridNode.renderContext.element);
            this.inflate.emit();
        } catch (e) {
            console.warn(e);
        }
        this._isInflating = false;
        this._setRowsTop();
        this._setRows();
        this._setSortDefs();
    }

    private _deflate(): void {
        try {
            const el: HTMLElement =
                this._viewContainerRef.element.nativeElement;
            el.removeChild(this.gridNode.renderContext.element);
        } catch (e) {
            console.warn(e);
        }
        try {
            this.renderer.destroy(this.gridNode);
            this.gridNode = null;
            this.renderer = null;
        } catch (e) {
            console.warn(e);
        }
    }
}

@NgModule({
    declarations: [GridContent, ExperimentalNgGridComponent],
    exports: [GridContent, ExperimentalNgGridComponent],
})
export class ExperimentalNgGridModule {}
